import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/landing/Footer";
import FloatingWhatsApp from "@/components/landing/FloatingWhatsApp";
import { CONTACT_EMAIL, LGPD_EMAIL, LOGO_URL } from "@/lib/constants";

const LAST_UPDATED = "outubro de 2026";

const SECTIONS = [
  {
    title: "1. Quem somos",
    paragraphs: [
      "Este site é operado pela Buzas Indústria e Comércio Ltda (“Buzas”), com sede na Rua Serra Redonda, 450 — Vila Industrial — São Paulo — SP, CEP 03244-150, que atua como controladora dos dados pessoais tratados aqui, nos termos da Lei nº 13.709/2018 (Lei Geral de Proteção de Dados Pessoais — LGPD).",
    ],
  },
  {
    title: "2. Dados que coletamos",
    paragraphs: [
      "Dados que você nos fornece: nome, empresa, telefone, e-mail e as informações que escrever na mensagem do formulário de orçamento, além dos dados que compartilhar ao falar conosco por WhatsApp, telefone ou e-mail.",
      "Dados de navegação: mediante o seu consentimento, utilizamos cookies e ferramentas de análise que registram como você interage com as páginas (páginas visitadas, cliques, rolagem, tipo de dispositivo e navegador, endereço IP e origem do acesso).",
    ],
  },
  {
    title: "3. Para que usamos os dados",
    paragraphs: [
      "Responder às suas solicitações de orçamento e de contato, e dar andamento às tratativas comerciais e técnicas.",
      "Enviar comunicações sobre nossos produtos e serviços, das quais você pode se descadastrar a qualquer momento.",
      "Entender como o site é utilizado, para corrigir falhas e melhorar o conteúdo e a experiência de navegação.",
      "Cumprir obrigações legais e regulatórias e exercer direitos em processos judiciais, administrativos ou arbitrais.",
    ],
  },
  {
    title: "4. Bases legais",
    paragraphs: [
      "Tratamos dados pessoais com fundamento nas seguintes hipóteses da LGPD: procedimentos preliminares a contrato, a pedido do titular (art. 7º, V), quando você solicita um orçamento; consentimento (art. 7º, I), para cookies e ferramentas de análise e para comunicações de marketing; legítimo interesse (art. 7º, IX), para o relacionamento comercial e a segurança do site; e cumprimento de obrigação legal ou regulatória (art. 7º, II).",
    ],
  },
  {
    title: "5. Cookies",
    paragraphs: [
      "Cookies são pequenos arquivos armazenados no seu navegador. Neste site eles são usados para lembrar sua escolha no aviso de cookies e, se você aceitar, para análise de navegação.",
      "Ao acessar o site pela primeira vez, você pode aceitar ou recusar os cookies de análise. Se recusar, o site continua funcionando normalmente. Você pode rever sua escolha a qualquer momento apagando os dados de navegação deste site no seu navegador.",
    ],
  },
  {
    title: "6. Compartilhamento",
    paragraphs: [
      "Não vendemos dados pessoais. Podemos compartilhá-los com prestadores de serviço que nos apoiam na operação do site e no atendimento (hospedagem, análise de navegação, ferramentas de comunicação e de e-mail), sempre limitados ao necessário para a prestação do serviço, e com autoridades públicas, quando exigido por lei.",
      "Alguns desses prestadores podem estar localizados fora do Brasil. Nesses casos, a transferência internacional de dados é feita de acordo com a LGPD.",
    ],
  },
  {
    title: "7. Armazenamento e segurança",
    paragraphs: [
      "Os dados são mantidos em serviços confiáveis, com medidas técnicas e administrativas para protegê-los contra acessos não autorizados, perda ou alteração. Eles são conservados pelo tempo necessário para as finalidades descritas nesta política, até que você solicite a exclusão ou enquanto houver obrigação legal de guarda.",
    ],
  },
  {
    title: "8. Seus direitos",
    paragraphs: [
      "Nos termos do art. 18 da LGPD, você pode solicitar a qualquer momento: a confirmação da existência de tratamento; o acesso aos seus dados; a correção de dados incompletos, inexatos ou desatualizados; a anonimização, o bloqueio ou a eliminação de dados desnecessários ou excessivos; a portabilidade; a eliminação dos dados tratados com base no consentimento; informações sobre o compartilhamento dos seus dados; e a revogação do consentimento.",
      "Você também tem o direito de apresentar reclamação à Autoridade Nacional de Proteção de Dados (ANPD).",
    ],
  },
  {
    title: "9. Alterações desta política",
    paragraphs: [
      "Esta política pode ser atualizada para refletir mudanças no site ou na legislação. A versão vigente estará sempre disponível nesta página, com a data da última atualização.",
    ],
  },
];

const PARAGRAPH_CLASS = "mt-4 text-[16px] leading-relaxed text-[#334155]";
const LINK_CLASS = "text-[var(--buzas-red)] hover:underline";

export default function PrivacyPolicy() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Política de Privacidade | Buzas";
    window.scrollTo(0, 0);
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <main
      data-testid="privacy-policy-root"
      className="min-h-screen bg-[var(--buzas-bg)] text-[var(--buzas-text)]"
    >
      <header
        data-testid="privacy-policy-header"
        className="bg-white border-b border-[var(--buzas-border)]"
      >
        <div className="container-buzas flex items-center justify-between h-[72px]">
          <a href="/" data-testid="privacy-policy-logo" className="shrink-0">
            <img
              src={LOGO_URL}
              alt="Buzas Laser Service"
              className="h-9 w-auto object-contain"
            />
          </a>
          <a
            href="/"
            data-testid="privacy-policy-back"
            className="inline-flex items-center gap-2 text-[14px] text-[#334155] hover:text-[var(--buzas-red)] transition-colors duration-200"
          >
            <ArrowLeft size={16} />
            Voltar ao site
          </a>
        </div>
      </header>

      <article className="container-buzas py-16 sm:py-20">
        <div className="max-w-3xl">
          <p className="eyebrow">LGPD</p>
          <h1 className="mt-3 text-[32px] sm:text-[40px] leading-tight">
            Política de Privacidade
          </h1>
          <p className="mt-3 text-[14px] text-[var(--buzas-muted)]">
            Última atualização: {LAST_UPDATED}
          </p>
          <p className="mt-8 text-[16px] leading-relaxed text-[#334155]">
            A Buzas respeita a sua privacidade. Esta política explica quais
            dados pessoais coletamos neste site, como os utilizamos e quais
            são os seus direitos.
          </p>

          {SECTIONS.map((section) => (
            <section key={section.title} className="mt-10">
              <h2 className="text-[20px] sm:text-[22px]">{section.title}</h2>
              {section.paragraphs.map((text) => (
                <p key={text} className={PARAGRAPH_CLASS}>
                  {text}
                </p>
              ))}
            </section>
          ))}

          <section className="mt-10">
            <h2 className="text-[20px] sm:text-[22px]">10. Contato</h2>
            <p className={PARAGRAPH_CLASS}>
              Para exercer seus direitos ou tirar dúvidas sobre esta política,
              escreva para{" "}
              <a
                href={`mailto:${LGPD_EMAIL}`}
                data-testid="privacy-policy-email"
                className={LINK_CLASS}
              >
                {LGPD_EMAIL}
              </a>
              . Pedidos de exclusão de dados são atendidos em até 5 dias
              úteis.
            </p>
            <p
              data-testid="privacy-policy-company"
              className="mt-8 pt-6 border-t border-[var(--buzas-border)] text-[14px] leading-relaxed text-[var(--buzas-muted)]"
            >
              Buzas Indústria e Comércio Ltda — Rua Serra Redonda, 450 — Vila
              Industrial — São Paulo — SP, CEP 03244-150 — +55 11 3385-8700 —{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className={LINK_CLASS}>
                {CONTACT_EMAIL}
              </a>
            </p>
          </section>
        </div>
      </article>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
